<script setup lang="ts">
const { t } = useI18n();
const { hasConsented, acceptAll } = useCookieConsent();

const isExpanded = ref(false);
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="cookie-banner">
        <div v-if="!hasConsented" class="cookie-banner">
          <div class="cookie-banner__container">
            <!-- Bar -->
            <div class="cookie-banner__bar">
              <p class="cookie-banner__text label">
                {{ t('CookieConsent.banner.text') }}
              </p>
              <div class="cookie-banner__actions">
                <AtomButton
                  appearance="secondary"
                  :action-text="t('CookieConsent.banner.customize')"
                  @click="isExpanded = !isExpanded" />
                <AtomButton
                  appearance="primary"
                  :action-text="t('CookieConsent.banner.acceptAll')"
                  @click="acceptAll" />
              </div>
            </div>

            <!-- Expandable options -->
            <Transition name="expand">
              <div v-if="isExpanded" class="cookie-banner__options">
                <p class="cookie-banner__hint label-sm">
                  {{ t('CookieConsent.settings.hint') }}
                </p>
                <MoleculeCookiePreferences show-descriptions />
              </div>
            </Transition>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped lang="scss">
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9000;
  background: white;
  box-shadow: 0 -4px 20px $color-black-transparent-200;
  border-top: 1px solid $color-gray-200;
}

.cookie-banner__container {
  max-width: 1200px;
  margin: 0 auto;
}

.cookie-banner__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.25rem 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1.25rem 1rem;
    gap: 1rem;
  }
}

.cookie-banner__text {
  margin: 0;
  color: $color-gray-600;
  flex: 1;
}

.cookie-banner__actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

.cookie-banner__options {
  padding: 0 2rem 1.5rem;
  border-top: 1px solid $color-gray-200;

  @media (max-width: 768px) {
    padding: 0 1rem 1.25rem;
  }
}

.cookie-banner__hint {
  margin: 1rem 0 0.5rem;
  color: $color-gray-500;
}

// Expand transition
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 600px;
  opacity: 1;
}

// Banner slide transition
.cookie-banner-enter-active,
.cookie-banner-leave-active {
  transition: transform 0.3s ease;
}

.cookie-banner-enter-from,
.cookie-banner-leave-to {
  transform: translateY(100%);
}
</style>
